import type { CSSProperties } from 'react';
import { darkTheme } from '../../theme/tokens';
import type { TileSize } from '../../types';
import type { ThumbDesc, ThumbKind } from './specThumbs';

const t = darkTheme;

// --- Spec-derived skeleton thumbnail (SC-2873) ---------------------------------
// Renders the wireframe the `specThumbs` module derives: mini blocks in each
// tile's span, one distinct pattern per component kind. No screenshots, no
// staleness: the thumbnail is derived the same way the board is rendered.

const SPAN: Record<TileSize, string> = { compact: 'span 3', half: 'span 6', full: 'span 12' };
const BLOCK_HEIGHT: Record<TileSize, number> = { compact: 9, half: 13, full: 13 };

function linesFor(kind: ThumbKind): number {
  if (kind === 'metric' || kind === 'donut') return 1;
  if (kind === 'bar' || kind === 'table') return 3;
  return 2;
}

function lineStyle(kind: ThumbKind, index: number): CSSProperties {
  if (kind === 'metric') {
    return { height: 2.5, width: '60%', margin: '0 auto', borderRadius: 1, background: t.text3 };
  }
  if (kind === 'bar') {
    const widths = ['85%', '60%', '35%'];
    return { height: 2, width: widths[index] ?? '50%', borderRadius: 1, background: t.chart1 };
  }
  if (kind === 'donut') {
    return {
      width: 8,
      height: 8,
      borderRadius: '50%',
      border: `2px solid ${t.chart1}`,
      borderBottomColor: t.chart2,
      background: 'none',
      margin: '0 auto',
    };
  }
  if (kind === 'table') {
    return { height: 1.5, width: '92%', margin: '0 auto', borderRadius: 1, background: t.border2 };
  }
  return { height: 1.5, width: '100%', borderRadius: 1, background: t.border2 };
}

/** The 66×44 wireframe. Decorative — hidden from AT (the row's name labels it). */
export function SpecThumbnail({ descs }: { descs: ThumbDesc[] }) {
  return (
    <div
      aria-hidden
      style={{
        width: 66,
        height: 44,
        flexShrink: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
        gap: 2,
        alignContent: 'start',
        padding: 4,
        background: t.sb,
        border: `1px solid ${t.border}`,
        borderRadius: 6,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {descs.slice(0, 8).map((desc, i) => (
        // Purely decorative static blocks — index keys can't misrender.
        <div
          key={i}
          style={{
            gridColumn: SPAN[desc.size],
            height: BLOCK_HEIGHT[desc.size],
            borderRadius: 2,
            background: t.surfaceAlt,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1.5,
            padding: 2,
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {Array.from({ length: linesFor(desc.kind) }, (_, line) => (
            <i key={line} style={{ display: 'block', ...lineStyle(desc.kind, line) }} />
          ))}
        </div>
      ))}
    </div>
  );
}
